import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { privateAxios } from "../utils/axios";

function GenerateReport() {
  const [inventoryReportData, setInventoryReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = 10;

  // ✅ Fetch real API data
  const handleGenerateInventoryReport = async () => {
    setIsLoading(true);

    try {
      const response = await privateAxios.get(
        `/products/generate-report`
      );

      if (response.data.success) {
        setInventoryReportData(response.data.data);
        setCurrentPage(1);
        // toast.success("Inventory report generated");
      }
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("Failed to generate report");
    } finally {
      setIsLoading(false);
    }
  };

  // Auto load on page open (optional)
  useEffect(() => {
    handleGenerateInventoryReport();
  }, []);

  // ✅ Local search filter
  const filteredProducts = useMemo(() => {
    if (!inventoryReportData?.products) return [];

    return inventoryReportData.products.filter((p) => {
      const keyword = searchTerm.toLowerCase();

      return (
        p.name?.toLowerCase().includes(keyword) ||
        p.sku?.toLowerCase().includes(keyword) ||
        p.category?.toLowerCase().includes(keyword)
      );
    });
  }, [inventoryReportData, searchTerm]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredProducts.length / pageSize);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredProducts.slice(start, start + pageSize);
  }, [filteredProducts, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center">
      <div className="flex flex-col gap-5 w-11/12">

        {/* Header */}
        <div className="bg-white rounded-xl border p-6 mt-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="font-bold text-xl">Products Report</h1>
              <p className="text-sm text-gray-500">
                Inventory report with search & pagination
              </p>
            </div>

            <button
              onClick={handleGenerateInventoryReport}
              disabled={isLoading}
              className="px-4 py-2 bg-black text-white rounded-lg disabled:opacity-50"
            >
              {isLoading ? "Generating..." : "Refresh Report"}
            </button>
          </div>
        </div>

        {/* Search */}
        {inventoryReportData && (
          <input
            type="text"
            placeholder="Search by name, SKU, category..."
            className="border p-2 rounded-lg w-full"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        )}

        {/* Table */}
        {inventoryReportData && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3">#</th>
                  <th className="p-3 text-left">Product</th>
                  <th className="p-3 text-left">SKU</th>
                  <th className="p-3 text-left">Category</th>
                  <th className="p-3 text-left">Purchase</th>
                  <th className="p-3 text-left">Warranty</th>
                  <th className="p-3 text-right">Price</th>
                </tr>
              </thead>

              <tbody>
                {paginatedProducts.map((product, index) => (
                  <tr key={product._id} className="border-t hover:bg-gray-50">
                    <td className="p-3 text-center">
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>

                    <td className="p-3 font-medium">{product.name}</td>

                    <td className="p-3">{product.sku}</td>

                    <td className="p-3 capitalize">{product.category}</td>

                    <td className="p-3">
                      <div>
                        {product?.purchaseId?.purchaseDate
                          ? moment(product.purchaseId.purchaseDate).format("DD/MM/YY")
                          : "-"}
                      </div>
                      <div className="text-xs text-gray-500">
                        Memo No:{product?.purchaseId?.memoNumber || "-"}
                      </div>
                    </td>

                    <td className="p-3">
                      {product?.warrantyExpireDate ? (
                        <>
                          <div>
                            {moment(product.warrantyExpireDate).format("DD/MM/YY")}
                          </div>
                          <div className="text-xs text-gray-500">
                            {moment(product.warrantyExpireDate).diff(moment(), "days") >= 0
                              ? `${moment(product.warrantyExpireDate).diff(moment(), "days")} days left`
                              : "Expired"}
                          </div>
                        </>
                      ) : (
                        "No Warranty"
                      )}
                    </td>

                    <td className="p-3 text-right">৳{product.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {inventoryReportData && totalPages > 1 && (
          <div className="flex flex-wrap gap-1 justify-center items-center mt-4">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:opacity-40"
            >
              &laquo;
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((page) => {
                if (totalPages <= 7) return true;
                if (page === 1 || page === totalPages) return true;
                if (Math.abs(page - currentPage) <= 2) return true;
                return false;
              })
              .reduce((acc, page, idx, arr) => {
                if (idx > 0 && page - arr[idx - 1] > 1) {
                  acc.push("...");
                }
                acc.push(page);
                return acc;
              }, [])
              .map((item, idx) =>
                item === "..." ? (
                  <span key={`ellipsis-${idx}`} className="px-2 py-1 text-gray-400">
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    onClick={() => handlePageChange(item)}
                    className={`px-3 py-1 border rounded ${
                      currentPage === item ? "bg-black text-white" : ""
                    }`}
                  >
                    {item}
                  </button>
                )
              )}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-40"
            >
              &raquo;
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default GenerateReport;